/**
 * EtsyReadinessStateSelector — bloc Settings → Atelier qui permet à
 * Stéphanie de choisir le readiness state (processing profile) Etsy
 * par défaut.
 *
 * Contexte : Etsy v3 a rendu `readiness_state_id` obligatoire sur les
 * listings physical/both en 2026-05 (HTTP 400 sinon). Stéphanie choisit
 * dans la liste de ses readiness state definitions Etsy ; le choix est
 * persisté dans `app_settings.etsy_default_readiness_state_id` et lu
 * par `etsy/adapter.publish` au prochain push.
 *
 * Comportement miroir de `EtsyShippingProfileSelector` :
 *   - Etsy connecté → dropdown live depuis Etsy API.
 *   - Etsy déconnecté ou 503 → fallback input number.
 *
 * Pas de différenciation par variante — atelier-wide.
 */

import { useMemo, useState } from 'react'
import { Loader2, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Select from '@/components/ui/Select'
import {
  useEtsyDefaultReadinessState,
  useEtsyReadinessStateDefinitions,
  useUpdateEtsyDefaultReadinessState,
} from '@/hooks/useAtelierSettings'
import { usePlatformConnections } from '@/hooks/usePlatformConnections'

export default function EtsyReadinessStateSelector() {
  const connectionsQuery = usePlatformConnections()
  const settingQuery = useEtsyDefaultReadinessState()
  const updateMutation = useUpdateEtsyDefaultReadinessState()

  const etsyConnection = useMemo(() => {
    const conns = connectionsQuery.data ?? []
    return conns.find((c) => c.platform === 'etsy' && !c.revoked_at) ?? null
  }, [connectionsQuery.data])

  const definitionsQuery = useEtsyReadinessStateDefinitions(etsyConnection?.id ?? null)

  const [selectedId, setSelectedId] = useState<number | null>(settingQuery.data?.value ?? null)
  const [manualInput, setManualInput] = useState<string>(
    settingQuery.data?.value !== null && settingQuery.data?.value !== undefined
      ? String(settingQuery.data.value)
      : '',
  )
  const [prevServerValue, setPrevServerValue] = useState<number | null | undefined>(
    settingQuery.data?.value,
  )
  if (settingQuery.data && settingQuery.data.value !== prevServerValue) {
    setPrevServerValue(settingQuery.data.value)
    setSelectedId(settingQuery.data.value)
    setManualInput(settingQuery.data.value !== null ? String(settingQuery.data.value) : '')
  }

  const isLoading = connectionsQuery.isLoading || settingQuery.isLoading
  const definitionsUnavailable = etsyConnection !== null && definitionsQuery.isError
  const showFallbackInput = etsyConnection === null || definitionsUnavailable

  const handleSave = () => {
    const valueToPost = showFallbackInput
      ? manualInput.trim() === ''
        ? null
        : Number.parseInt(manualInput, 10)
      : selectedId
    if (valueToPost !== null && (!Number.isFinite(valueToPost) || valueToPost < 1)) {
      toast.error('ID readiness state invalide — saisis un entier ≥ 1')
      return
    }
    updateMutation.mutate(
      { value: valueToPost },
      {
        onSuccess: () => {
          toast.success(
            valueToPost === null
              ? 'Readiness state par défaut effacé'
              : `Readiness state par défaut mis à jour (#${valueToPost})`,
          )
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Sauvegarde impossible')
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div>
        <header className="settings-section-header">
          <div>
            <h2 className="ui-h4 settings-section-title">Délai de fabrication (Etsy)</h2>
          </div>
        </header>
        <p className="atelier-shipping-loading">
          <Loader2 className="atelier-shipping-spinner" size={16} />
          Chargement…
        </p>
      </div>
    )
  }

  return (
    <div>
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Délai de fabrication (Etsy)</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Etsy exige maintenant un <strong>processing profile</strong> (alias{' '}
            <em>readiness state</em>) sur chaque listing physique. Sélectionne le délai que tu
            utilises par défaut — il s'applique à toutes les publications print, limited et
            original.
          </p>
        </div>
      </header>

      {etsyConnection === null && (
        <div className="ui-alert ui-alert--info" role="status">
          Etsy n'est pas connecté. Connecte ton compte Etsy depuis l'onglet{' '}
          <strong>Diffusion</strong> pour voir la liste — ou saisis manuellement l'ID ci-dessous.
        </div>
      )}

      {definitionsUnavailable && (
        <div className="ui-alert ui-alert--info" role="status">
          Impossible de lire les readiness states depuis Etsy (token expiré ou API indisponible).
          Reconnecte Etsy, ou saisis l'ID manuellement ci-dessous.
        </div>
      )}

      <div className="atelier-shipping-fieldset">
        {!showFallbackInput && definitionsQuery.data && definitionsQuery.data.length === 0 && (
          <div className="ui-alert ui-alert--warning" role="status">
            <p className="ui-body">
              <strong>Aucun processing profile trouvé côté Etsy.</strong> Crée-en un d'abord depuis
              ton seller dashboard, puis recharge cette page.
            </p>
            <p className="atelier-shipping-link">
              <a
                href="https://www.etsy.com/your/shops/me/tools/shipping-profiles"
                target="_blank"
                rel="noreferrer noopener"
              >
                Ouvrir Etsy → Shipping settings → onglet "Processing profiles"
                <ExternalLink size={12} />
              </a>
            </p>
          </div>
        )}

        {!showFallbackInput && definitionsQuery.data && definitionsQuery.data.length > 0 && (
          <Field
            label="Readiness state par défaut"
            helper="Sélectionne le profil de délai que tu utilises pour tes tirages."
          >
            {(controlProps) => (
              <Select
                {...controlProps}
                value={selectedId === null ? '' : String(selectedId)}
                onChange={(e) =>
                  setSelectedId(e.target.value === '' ? null : Number.parseInt(e.target.value, 10))
                }
              >
                <option value="">— Aucun profile —</option>
                {definitionsQuery.data.map((d) => (
                  <option key={d.readiness_state_id} value={d.readiness_state_id}>
                    {d.processing_days_display_label ??
                      (d.min_processing_days && d.max_processing_days
                        ? `${d.min_processing_days}-${d.max_processing_days} jours`
                        : `Profile #${d.readiness_state_id}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {showFallbackInput && (
          <>
            <Field
              label="ID du readiness state Etsy"
              helper="Identifiant numérique du processing profile (visible dans l'URL côté Etsy seller)."
            >
              {(controlProps) => (
                <Input
                  {...controlProps}
                  type="number"
                  min={1}
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="ex: 12345"
                />
              )}
            </Field>
            <p className="atelier-shipping-link">
              <a
                href="https://www.etsy.com/your/shops/me/tools/shipping-profiles"
                target="_blank"
                rel="noreferrer noopener"
              >
                Ouvrir les réglages d'expédition Etsy (onglet Processing profiles)
                <ExternalLink size={12} />
              </a>
            </p>
          </>
        )}

        <div className="atelier-shipping-actions">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Sauvegarde…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  )
}
